/**
 * Hex viewer — sector-on-demand, annotated, with structure overlay.
 *
 * Renders 16-byte rows: 8-digit offset + 16 hex pairs + ASCII gutter.
 * Caller can apply "highlights" — named byte ranges that get coloured + clickable.
 *
 * Surface: window.__DiskImage.HexViewer
 *   var hv = new HexViewer(reader, container, opts);
 *   hv.goto(lba)            — center view on sector LBA
 *   hv.gotoOffset(byteOff)  — center view on byte offset
 *   hv.highlight([{name,start,end,color?,onClick?}])
 *   hv.dispose()
 *
 * Performance: virtualised — only renders rows visible in viewport ± 16 rows
 * buffer, even for multi-GB images.
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.HexViewer) return;

    var ROW_H = 18; // px
    var BUFFER_ROWS = 24;
    var BYTES_PER_ROW = 16;

    function HexViewer(reader, container, opts) {
        this.reader = reader;
        this.container = container;
        this.opts = opts || {};
        this.size = reader.size;
        this.totalRows = Math.ceil(this.size / BYTES_PER_ROW);
        this._highlights = [];
        this._activeHighlight = null;
        this._cache = new Map(); // rowIdx → {hex, ascii, raw}
        this._scroll = null;
        this._inner = null;
        this._statusBar = null;
        this._goToInput = null;
        this._render();
    }

    HexViewer.prototype._render = function () {
        var self = this;
        var c = this.container;
        c.innerHTML = '';
        c.classList.add('hex-viewer');
        if (!c.style.position) c.style.position = 'relative';

        // Top bar: offset jump + structure picker + status
        var top = document.createElement('div');
        top.className = 'hex-top';
        top.style.cssText = 'display:flex;gap:8px;align-items:center;padding:6px 8px;border-bottom:1px solid var(--lg-border, #2a2a2a);font-size:12px;flex-wrap:wrap;';
        top.innerHTML = '<label style="display:flex;align-items:center;gap:4px">' +
            '<span>Goto</span>' +
            '<input class="hex-goto" placeholder="hex byte offset / sector # / LBA*2048" ' +
                 'style="padding:3px 6px;border-radius:4px;border:1px solid var(--lg-border,#2a2a2a);background:var(--lg-surface-3,#1a1a1a);color:var(--lg-text,#e5e5e5);font-family:ui-monospace,monospace;font-size:12px;width:200px"/>' +
            '</label>' +
            '<span class="hex-status" style="margin-left:auto;color:var(--lg-text-muted,#888)"></span>';
        c.appendChild(top);
        this._goToInput = top.querySelector('.hex-goto');
        this._statusBar = top.querySelector('.hex-status');

        this._goToInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') self._onGoto();
        });

        // Scroll viewport
        var scroll = document.createElement('div');
        scroll.className = 'hex-scroll';
        scroll.style.cssText = 'overflow-y:auto;height:' + (this.opts.height || 360) + 'px;font-family:ui-monospace,Menlo,monospace;font-size:12px;line-height:' + ROW_H + 'px;background:var(--lg-surface-2,#111);';
        c.appendChild(scroll);
        this._scroll = scroll;

        var inner = document.createElement('div');
        inner.style.cssText = 'position:relative;height:' + (this.totalRows * ROW_H) + 'px;';
        scroll.appendChild(inner);
        this._inner = inner;

        // Highlight legend
        var legend = document.createElement('div');
        legend.className = 'hex-legend';
        legend.style.cssText = 'padding:6px 8px;border-top:1px solid var(--lg-border,#2a2a2a);font-size:12px;display:flex;gap:8px;flex-wrap:wrap;color:var(--lg-text-muted,#888)';
        c.appendChild(legend);
        this._legend = legend;

        scroll.addEventListener('scroll', function () {
            self._renderRows();
        });
        this._renderRows();
        this._updateStatus(0);
    };

    HexViewer.prototype._onGoto = function () {
        var v = String(this._goToInput.value || '').trim();
        if (!v) return;
        var off;
        if (/^lba:?\s*\d+$/i.test(v)) {
            off = parseInt(v.replace(/^lba:?\s*/i, ''), 10) * 2048;
        } else if (/^0x[0-9a-f]+$/i.test(v)) {
            off = parseInt(v, 16);
        } else if (/^\d+$/.test(v)) {
            off = parseInt(v, 10);
        } else if (/^[0-9a-f]+$/i.test(v) && v.length > 4) {
            off = parseInt(v, 16);
        } else {
            return;
        }
        this.gotoOffset(off);
    };

    HexViewer.prototype.gotoOffset = function (byteOff) {
        if (byteOff == null || byteOff < 0) byteOff = 0;
        if (byteOff >= this.size) byteOff = this.size - 1;
        var row = Math.floor(byteOff / BYTES_PER_ROW);
        var top = row * ROW_H - 100;
        if (top < 0) top = 0;
        this._scroll.scrollTop = top;
    };
    HexViewer.prototype.goto = function (lba) { this.gotoOffset(lba * 2048); };

    HexViewer.prototype._updateStatus = function (offset) {
        var lba = Math.floor(offset / 2048);
        var sectorOff = offset - lba * 2048;
        if (this._statusBar) {
            this._statusBar.textContent = 'offset 0x' + offset.toString(16).padStart(8, '0') +
                ' (' + offset + ') · LBA ' + lba + ' +0x' + sectorOff.toString(16).padStart(3, '0');
        }
    };

    HexViewer.prototype.highlight = function (highlights) {
        this._highlights = (highlights || []).slice();
        this._renderLegend();
        // Re-render visible rows
        this._cache.clear();
        this._renderRows();
    };

    HexViewer.prototype._renderLegend = function () {
        var self = this;
        if (!this._legend) return;
        if (!this._highlights.length) {
            this._legend.innerHTML = '';
            return;
        }
        var html = '';
        this._highlights.forEach(function (h, i) {
            var color = h.color || _palette(i);
            html += '<span class="hex-hl-chip" data-hl="' + i + '" style="cursor:pointer;display:inline-flex;align-items:center;gap:4px;padding:2px 6px;border-radius:6px;background:' + color + '22;border:1px solid ' + color + '">' +
                '<span style="width:8px;height:8px;background:' + color + ';border-radius:2px"></span>' +
                self._esc(h.name) + '</span>';
        });
        this._legend.innerHTML = html;
        this._legend.querySelectorAll('.hex-hl-chip').forEach(function (chip) {
            chip.addEventListener('click', function () {
                var idx = parseInt(chip.getAttribute('data-hl'), 10);
                var h = self._highlights[idx];
                if (h) {
                    self.gotoOffset(h.start);
                    self._activeHighlight = h;
                    self._cache.clear();
                    self._renderRows();
                }
            });
        });
    };

    HexViewer.prototype._renderRows = function () {
        var self = this;
        var top = this._scroll.scrollTop;
        var height = this._scroll.clientHeight;
        var firstRow = Math.max(0, Math.floor(top / ROW_H) - BUFFER_ROWS);
        var lastRow = Math.min(this.totalRows - 1, Math.ceil((top + height) / ROW_H) + BUFFER_ROWS);

        // Update status with first visible byte
        this._updateStatus(firstRow * BYTES_PER_ROW);

        // Promise chain to fetch all needed bytes
        var startOff = firstRow * BYTES_PER_ROW;
        var endOff = Math.min(this.size, (lastRow + 1) * BYTES_PER_ROW);
        var len = endOff - startOff;
        if (len <= 0) { this._inner.innerHTML = ''; return; }

        this.reader.bytes(startOff, len).then(function (bytes) {
            var html = '';
            for (var r = firstRow; r <= lastRow; r++) {
                var rowOff = r * BYTES_PER_ROW;
                var inOff = rowOff - startOff;
                var rowBytes = bytes.subarray(inOff, Math.min(inOff + BYTES_PER_ROW, bytes.length));
                html += self._renderRow(r, rowOff, rowBytes);
            }
            self._inner.innerHTML = html;
            // Wire byte clicks
            self._inner.querySelectorAll('.hex-byte').forEach(function (sp) {
                sp.addEventListener('click', function () {
                    var off = parseInt(sp.getAttribute('data-off'), 10);
                    self._showByteInfo(off);
                });
            });
        }).catch(function () {});
    };

    HexViewer.prototype._renderRow = function (rowIdx, rowOff, bytes) {
        var offHex = '0x' + rowOff.toString(16).padStart(8, '0');
        var hex = '';
        var ascii = '';
        for (var i = 0; i < BYTES_PER_ROW; i++) {
            if (i < bytes.length) {
                var b = bytes[i];
                var hexStr = b.toString(16).padStart(2, '0');
                var hl = this._hlFor(rowOff + i);
                var style = hl ? ('background:' + hl.color + '33;border-bottom:2px solid ' + hl.color) : '';
                hex += '<span class="hex-byte" data-off="' + (rowOff + i) + '" title="' + (hl ? this._esc(hl.name) : '') + '" style="' + style + ';padding:0 1px;cursor:pointer">' + hexStr + '</span>';
                ascii += '<span class="hex-byte" data-off="' + (rowOff + i) + '" style="' + style + '">' + (b >= 32 && b < 127 ? this._esc(String.fromCharCode(b)) : '·') + '</span>';
            } else {
                hex += '<span style="opacity:.3">  </span>';
                ascii += ' ';
            }
            if (i === 7) hex += ' ';
            hex += ' ';
        }
        return '<div class="hex-row" style="position:absolute;top:' + (rowIdx * ROW_H) + 'px;left:0;right:0;height:' + ROW_H + 'px;padding:0 8px;white-space:pre;display:flex;gap:14px">' +
            '<span style="color:var(--lg-text-muted,#888);user-select:none">' + offHex + '</span>' +
            '<span style="flex:0 0 auto">' + hex + '</span>' +
            '<span style="color:var(--lg-text-muted,#888)">' + ascii + '</span>' +
            '</div>';
    };

    HexViewer.prototype._hlFor = function (offset) {
        for (var i = 0; i < this._highlights.length; i++) {
            var h = this._highlights[i];
            if (offset >= h.start && offset < h.end) {
                if (!h.color) h.color = _palette(i);
                return h;
            }
        }
        return null;
    };

    HexViewer.prototype._showByteInfo = function (offset) {
        var hl = this._hlFor(offset);
        var info = 'offset 0x' + offset.toString(16).padStart(8, '0') + ' (' + offset + ')';
        if (hl) info += ' — ' + hl.name + (hl.value != null ? ' = ' + hl.value : '');
        if (this._statusBar) this._statusBar.textContent = info;
    };

    HexViewer.prototype._esc = function (s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    };

    HexViewer.prototype.dispose = function () {
        this._cache.clear();
        if (this.container) this.container.innerHTML = '';
    };

    function _palette(i) {
        var c = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
        return c[i % c.length];
    }

    /**
     * Helper: build highlights for an MBR partition table (4 entries × 16 bytes @ offset 446).
     */
    function mbrHighlights(mbr) {
        var hl = [];
        hl.push({ name: 'MBR bootloader', start: 0, end: 446, color: '#6b7280' });
        if (mbr && mbr.partitions) {
            mbr.partitions.forEach(function (p, i) {
                var off = 446 + p.index * 16;
                hl.push({ name: 'Partition ' + (p.index + 1) + ' (' + p.typeName + ')', start: off, end: off + 16, color: _palette(i + 1) });
            });
        }
        hl.push({ name: 'MBR signature 0x55AA', start: 510, end: 512, color: '#10b981' });
        return hl;
    }

    /** Helper: PVD highlights @ sector 16 (offset 0x8000..0x8800) */
    function pvdHighlights() {
        var base = 16 * 2048;
        return [
            { name: 'Type (0x01 = PVD)', start: base, end: base + 1, color: '#3b82f6' },
            { name: 'Standard ID "CD001"', start: base + 1, end: base + 6, color: '#10b981' },
            { name: 'Version', start: base + 6, end: base + 7 },
            { name: 'System Identifier', start: base + 8, end: base + 40 },
            { name: 'Volume Identifier', start: base + 40, end: base + 72, color: '#f59e0b' },
            { name: 'Volume Space Size', start: base + 80, end: base + 88 },
            { name: 'Logical Block Size', start: base + 128, end: base + 132 },
            { name: 'Path Table Size', start: base + 132, end: base + 140 },
            { name: 'Path Table L LBA', start: base + 140, end: base + 144 },
            { name: 'Path Table M LBA', start: base + 148, end: base + 152 },
            { name: 'Root Directory Record', start: base + 156, end: base + 156 + 34, color: '#8b5cf6' }
        ];
    }

    /** Helper: GPT header highlights @ LBA 1 */
    function gptHighlights() {
        var base = 512;
        return [
            { name: 'GPT signature "EFI PART"', start: base, end: base + 8, color: '#10b981' },
            { name: 'Revision', start: base + 8, end: base + 12 },
            { name: 'Header size', start: base + 12, end: base + 16 },
            { name: 'Header CRC32', start: base + 16, end: base + 20, color: '#f59e0b' },
            { name: 'Disk GUID', start: base + 56, end: base + 72, color: '#8b5cf6' },
            { name: 'Partition entry LBA', start: base + 72, end: base + 80 },
            { name: 'Number of partition entries', start: base + 80, end: base + 84 },
            { name: 'Size of partition entry', start: base + 84, end: base + 88 },
            { name: 'Entries CRC32', start: base + 88, end: base + 92, color: '#f59e0b' }
        ];
    }

    w.__DiskImage.HexViewer = HexViewer;
    w.__DiskImage.HexHighlights = { mbr: mbrHighlights, pvd: pvdHighlights, gpt: gptHighlights };
})(window);
